import React, { useMemo } from 'react';
import type { MentorVideos } from '../../../types/mentor';

/**
 * Custom hook that calculates CSS properties to position and scale a video
 * so that the detected face is centered in the viewport.
 *
 * The hook uses face detection coordinates to:
 * 1. Center the face in the viewport
 * 2. Dynamically scale the video based on face size (zoom in/out)
 * 3. Apply progressive asymmetric adjustments for off-center faces
 * 4. Clamp the translation to prevent showing video edges
 *
 * Face position is in normalized coordinates (0-1 range):
 * - face_position[0] = x (left edge of face)
 * - face_position[1] = y (top edge of face)
 * - face_position[2] = width of face
 * - face_position[3] = height of face
 *
 * @param hdVideo - Video metadata including face_position coordinates
 * @returns CSS properties object for styling the video element
 */
export const useFacePosition = (
  hdVideo: MentorVideos | undefined,
): React.CSSProperties => {
  return useMemo(() => {
    // Default video size is 200% (2x the container) to allow panning/cropping
    const defaultVideoSizePercent = 200;
    // Default centering transform when no face position is available
    const defaultTransform = 'translate(-50%, -50%)';

    // Common CSS properties applied to all video elements
    const common = {
      width: `${defaultVideoSizePercent}%`,
      height: `${defaultVideoSizePercent}%`,
      objectFit: 'cover' as const, // Crop video to fill container
      position: 'absolute' as const, // Position relative to container
      top: 0,
      left: 0,
      maxWidth: 'none', // Allow video to exceed container bounds
    };

    // Fallback: If no video data or face position, use default centering
    if (!hdVideo || !hdVideo?.face_position) {
      return {
        ...common,
        transform: defaultTransform,
      };
    }

    // Destructure face position coordinates
    // face_position is [x, y, width, height] where values are normalized (0-1)
    // x, y: top-left corner of face bounding box
    // width, height: dimensions of face bounding box
    const [x, y, width, height] = hdVideo.face_position;

    // Calculate the center point of the detected face
    const faceCenterX = x + width / 2;
    const faceCenterY = y + height / 2;

    // Calculate video size dynamically based on face height
    // Baseline: face height of ~0.2 should result in 200% video size
    // Larger faces (closer to camera): smaller video size (zoom out)
    // Smaller faces (further away): larger video size (zoom in)
    const baselineFaceHeight = 0.2;
    const sizeRatio = baselineFaceHeight / Math.max(height, 0.1);
    // Clamp to reasonable bounds (130% to 250%) for stable framing
    const videoSizePercent = Math.max(130, Math.min(250, 200 * sizeRatio));

    // Calculate base translation to center the face
    // Formula: ((50 - faceCenterPosition * videoSize) / videoSize) * 100
    // This converts face position to the translation needed to center it
    const baseTranslateX =
      ((50 - faceCenterX * videoSizePercent) / videoSizePercent) * 100;
    const baseTranslateY =
      ((50 - faceCenterY * videoSizePercent) / videoSizePercent) * 100;

    // Calculate how far the face is from the center of the frame
    // Values range from -0.5 (far left/top) to +0.5 (far right/bottom)
    const offsetFromCenterX = faceCenterX - 0.5;
    const offsetFromCenterY = faceCenterY - 0.5;

    // Apply progressive asymmetric adjustment: faces further from center get stronger correction
    // X-axis: 1.5x multiplier for horizontal positioning (more aggressive)
    // Y-axis: 0.6x multiplier for vertical positioning (gentler, more natural)
    // This creates more natural framing with stronger horizontal centering
    const adjustmentFactorX = 1 + Math.abs(offsetFromCenterX) * 1.5;
    const adjustmentFactorY = 1 + Math.abs(offsetFromCenterY) * 0.6;

    // Apply the adjustment factors to the base translation
    const translateXPercent = baseTranslateX * adjustmentFactorX;
    const translateYPercent = baseTranslateY * adjustmentFactorY;

    // Safety check: if calculations resulted in invalid numbers, use default
    if (isNaN(translateXPercent) || isNaN(translateYPercent)) {
      return {
        ...common,
        transform: defaultTransform,
      };
    }

    // Calculate clamping bounds to prevent showing video edges
    // minTranslate: maximum negative translation (e.g., -50% for 200% video)
    // maxTranslate: no positive translation allowed (would show edges)
    const minTranslate = -(videoSizePercent - 100) / 2;
    const maxTranslate = 0;

    // Clamp translation values to valid range
    // This ensures we never translate so far that we see the video edges
    const clampedX = Math.max(
      minTranslate,
      Math.min(maxTranslate, translateXPercent),
    );
    const clampedY = Math.max(
      minTranslate,
      Math.min(maxTranslate, translateYPercent),
    );

    // Build final style object with calculated positioning
    const style = {
      ...common,
      width: `${videoSizePercent}%`,
      height: `${videoSizePercent}%`,
      // Apply the clamped translation to position the face in center
      transform: `translate(${clampedX.toFixed(2)}%, ${clampedY.toFixed(2)}%)`,
    };
    return style;
  }, [hdVideo]);
};
